import { useEffect, type RefObject } from "react";

type Ref = RefObject<HTMLElement | null>;

/**
 * Fires `onOutside` whenever a mousedown lands outside the referenced element.
 * Pass `enabled=false` to skip the listener (e.g. when the popover is closed).
 *
 * For portaled popovers, the popover lives outside its trigger's DOM subtree,
 * so a click on the trigger looks "outside" and would race the trigger's own
 * onClick toggle. Pass the trigger as an additional ignored ref (or array of
 * refs) to suppress that.
 */
export function useClickOutside(
  ref: Ref,
  onOutside: () => void,
  enabled: boolean = true,
  ignoredRefs?: Ref | Ref[],
): void {
  useEffect(() => {
    if (!enabled) return;
    const extras: Ref[] = ignoredRefs == null
      ? []
      : Array.isArray(ignoredRefs)
        ? ignoredRefs
        : [ignoredRefs];
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      for (const r of extras) {
        if (r.current?.contains(target)) return;
      }
      onOutside();
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [ref, onOutside, enabled, ignoredRefs]);
}
